import { useState, useEffect } from "react";
import IntroEnvelope from "./IntroEnvelope";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = "intro_seen";

/**
 * IntroGate — wraps the home page so the envelope intro is shown
 * on first load. After the user clicks "Pincha para entrar", the
 * children (home) are rendered. No router redirect, no reload.
 * If the backend flag `enabled` is false, the home is rendered
 * directly (no intro).
 *
 * Uses sessionStorage so the intro is shown ONLY on the first
 * visit of the tab/session. Clicking "Inicio" from the header
 * (internal navigation) does NOT replay the intro. A full page
 * reload or a new tab does.
 */
const IntroGate = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // If the user already dismissed the intro in this session,
    // skip the network call entirely — show home immediately.
    const alreadySeen = (() => {
      try { return sessionStorage.getItem(SEEN_KEY) === "1"; }
      catch { return false; }
    })();
    if (alreadySeen) {
      setShowIntro(false);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/intro/config`);
        if (!res.ok) throw new Error("no config");
        const data = await res.json();
        if (cancelled) return;
        setConfig(data);
        setShowIntro(data.enabled !== false);
      } catch (e) {
        if (!cancelled) {
          setShowIntro(false);
          setConfig(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEnter = () => {
    try { sessionStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
    setShowIntro(false);
  };

  if (loading) {
    return <div style={{ minHeight: "100vh", background: "#0a0a0a" }} />;
  }

  if (showIntro) {
    return <IntroEnvelope config={config} onEnter={handleEnter} />;
  }

  return children;
};

export default IntroGate;
